import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MapPin, ArrowSquareOut, UploadSimple, Camera, PaperPlaneTilt, User, CheckCircle, CurrencyDollar, DotsSixVertical, Funnel, X } from "@phosphor-icons/react";
import client from "../lib/api";
import { fmtDate } from "../lib/helpers";

const COLUMNS = [
  { key: "scheduled", label: "Scheduled", hint: "Upcoming", tone: "#2B4C7E", droppable: true },
  { key: "in_progress", label: "In Progress", hint: "Shooting / editing", tone: "#B87333", droppable: true },
  { key: "awaiting", label: "Awaiting Delivery", hint: "Shot — post the link", tone: "#8A5028", droppable: true },
  { key: "delivered", label: "Delivered", hint: "Link is live", tone: "#1A4331", droppable: false },
  { key: "closed", label: "Paid · Closed", hint: "Delivered & paid", tone: "#1A4331", droppable: false },
];

const STATUS_FOR = { scheduled: "scheduled", in_progress: "in_progress", awaiting: "completed" };

function stageOf(p) {
  if (p.status === "cancelled") return null;
  if (p.client_paid) return "closed";
  if (p.deliverables_ready) return "delivered";
  if (p.status === "completed") return "awaiting";
  if (p.status === "in_progress") return "in_progress";
  return "scheduled";
}

function LinkChip({ href, icon: Icon, label, testid, tone }) {
  if (!href) return (
    <span className="inline-flex items-center gap-1 text-[11px] text-[#B7B2A8] border border-dashed border-[#E9DDC6] rounded-[4px] px-2 py-1">
      <Icon size={12} weight="bold" />No {label}
    </span>
  );
  return (
    <a href={href} target="_blank" rel="noreferrer" data-testid={testid} onClick={(e) => e.stopPropagation()}
      className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-[4px] px-2 py-1 border transition-colors"
      style={{ color: tone, borderColor: `${tone}44` }}>
      <Icon size={12} weight="bold" />{label}<ArrowSquareOut size={11} weight="bold" />
    </a>
  );
}

export default function DeliverablesKanban({ onOpenProperty }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fAgent, setFAgent] = useState("all");
  const [fPhotog, setFPhotog] = useState("all");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");

  const load = useCallback(() => client.get("/deliverables").then((r) => { setItems(r.data.items || []); setLoading(false); }).catch(() => setLoading(false)), []);
  useEffect(() => { load(); const iv = setInterval(load, 6000); return () => clearInterval(iv); }, [load]);

  const agentOpts = [...new Set(items.map((p) => p.agent || "Unassigned"))].sort();
  const photogOpts = [...new Set(items.map((p) => p.photographer).filter(Boolean))].sort();
  const hasFilters = fAgent !== "all" || fPhotog !== "all" || !!fFrom || !!fTo;
  const clearFilters = () => { setFAgent("all"); setFPhotog("all"); setFFrom(""); setFTo(""); };

  const filtered = items.filter((p) => {
    if (fAgent !== "all" && (p.agent || "Unassigned") !== fAgent) return false;
    if (fPhotog !== "all" && p.photographer !== fPhotog) return false;
    if (fFrom && (!p.shoot_date || new Date(p.shoot_date) < new Date(fFrom))) return false;
    if (fTo && (!p.shoot_date || new Date(p.shoot_date) > new Date(fTo + "T23:59:59"))) return false;
    return true;
  });

  const grouped = COLUMNS.reduce((acc, c) => { acc[c.key] = []; return acc; }, {});
  filtered.forEach((p) => { const s = stageOf(p); if (s && grouped[s]) grouped[s].push(p); });

  const move = async (col, p) => {
    const cur = stageOf(p);
    if (cur === col.key) return;
    if (!col.droppable) {
      toast.info(col.key === "delivered"
        ? "Delivered updates automatically once a deliverables link is posted on the shoot."
        : "Paid · Closed updates automatically when the agent's invoice is paid.");
      return;
    }
    setBusy(true);
    try {
      await client.patch(`/properties/${p.property_id}`, { status: STATUS_FOR[col.key] });
      setItems((prev) => prev.map((x) => x.property_id === p.property_id ? { ...x, status: STATUS_FOR[col.key], deliverables_ready: col.key === "awaiting" ? x.deliverables_ready : false } : x));
      toast.success(`Moved to ${col.label}`);
      load();
    } catch {
      toast.error("Couldn't move that shoot.");
    }
    setBusy(false);
  };

  if (loading) return <p className="text-[#5C5A58]" data-testid="kanban-loading">Loading board…</p>;

  return (
    <div data-testid="deliverables-kanban">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <p className="text-sm text-[#5C5A58]">Drag a shoot across the pipeline. <span className="text-[#8A6D1B]">Delivered</span> &amp; <span className="text-[#2E4F3B]">Paid</span> update on their own.</p>
        <span className="text-xs text-[#9A968E]" data-testid="kanban-total">{filtered.filter((p) => stageOf(p)).length}{hasFilters ? ` of ${items.filter((p) => stageOf(p)).length}` : ""} active shoots</span>
      </div>
      <div className="flex items-end gap-2 flex-wrap mb-4 pb-4 border-b border-[#E9DDC6]" data-testid="kanban-filters">
        <div className="flex items-center gap-1.5 text-[#8A6D1B] mr-1"><Funnel size={15} weight="duotone" /><span className="text-[11px] uppercase tracking-[0.12em] font-semibold">Filter</span></div>
        <div className="flex flex-col gap-0.5">
          <label className="text-[10px] text-[#9A968E] uppercase tracking-wide">Agent</label>
          <select data-testid="kanban-filter-agent" value={fAgent} onChange={(e) => setFAgent(e.target.value)}
            className="h-8 rounded-[4px] border border-[#E9DDC6] bg-white px-2 text-sm text-[#2A0A0A] focus:border-[#B08D57] outline-none max-w-[180px]">
            <option value="all">All agents</option>
            {agentOpts.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-0.5">
          <label className="text-[10px] text-[#9A968E] uppercase tracking-wide">Photographer</label>
          <select data-testid="kanban-filter-photographer" value={fPhotog} onChange={(e) => setFPhotog(e.target.value)}
            className="h-8 rounded-[4px] border border-[#E9DDC6] bg-white px-2 text-sm text-[#2A0A0A] focus:border-[#B08D57] outline-none max-w-[180px]">
            <option value="all">All photographers</option>
            {photogOpts.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-0.5">
          <label className="text-[10px] text-[#9A968E] uppercase tracking-wide">From</label>
          <input type="date" data-testid="kanban-filter-from" value={fFrom} onChange={(e) => setFFrom(e.target.value)}
            className="h-8 rounded-[4px] border border-[#E9DDC6] bg-white px-2 text-sm text-[#2A0A0A] focus:border-[#B08D57] outline-none" />
        </div>
        <div className="flex flex-col gap-0.5">
          <label className="text-[10px] text-[#9A968E] uppercase tracking-wide">To</label>
          <input type="date" data-testid="kanban-filter-to" value={fTo} onChange={(e) => setFTo(e.target.value)}
            className="h-8 rounded-[4px] border border-[#E9DDC6] bg-white px-2 text-sm text-[#2A0A0A] focus:border-[#B08D57] outline-none" />
        </div>
        {hasFilters && (
          <button data-testid="kanban-filter-clear" onClick={clearFilters}
            className="h-8 inline-flex items-center gap-1 rounded-[4px] border border-[#E9DDC6] bg-white px-2.5 text-xs text-[#7E1119] hover:border-[#7E1119]/40 transition-colors">
            <X size={13} weight="bold" />Clear
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4" data-testid="kanban-board">
        {COLUMNS.map((col) => (
          <div key={col.key} data-testid={`kanban-col-${col.key}`}
            onDragOver={(e) => { if (col.droppable && dragId) { e.preventDefault(); setOverCol(col.key); } }}
            onDragLeave={() => setOverCol((c) => (c === col.key ? null : c))}
            onDrop={(e) => { e.preventDefault(); setOverCol(null); const p = items.find((x) => x.property_id === dragId); if (p) move(col, p); setDragId(null); }}
            className={`rounded-[6px] border bg-[#FBFAF7] overflow-hidden transition-colors ${overCol === col.key ? "border-[#B08D57] bg-[#B08D57]/[0.06]" : "border-[#E9DDC6]"}`}>
            <div style={{ height: 3, background: col.tone }} />
            <div className="px-3 py-2.5 border-b border-[#E9DDC6] sticky top-0 bg-[#FBFAF7] z-10">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold uppercase tracking-[0.12em]" style={{ color: col.tone }}>{col.label}</span>
                <span className="text-xs tabular-nums text-[#9A968E]" data-testid={`kanban-count-${col.key}`}>{grouped[col.key].length}</span>
              </div>
              <p className="text-[10px] text-[#B7B2A8] mt-0.5">{col.hint}</p>
            </div>
            <div className="p-2 space-y-2 min-h-[120px]">
              {grouped[col.key].length === 0 && <p className="text-[11px] text-[#C2BCB2] text-center py-6">Nothing here</p>}
              {grouped[col.key].map((p) => (
                <motion.div key={p.property_id} layout draggable data-testid={`kanban-card-${p.property_id}`}
                  onDragStart={() => setDragId(p.property_id)} onDragEnd={() => { setDragId(null); setOverCol(null); }}
                  onClick={() => onOpenProperty(p.property_id)}
                  className={`group surface-kpi p-3 cursor-grab active:cursor-grabbing ${dragId === p.property_id ? "opacity-50" : ""} ${busy ? "pointer-events-none" : ""}`}>
                  <div className="flex items-start gap-1.5 relative z-10">
                    <DotsSixVertical size={15} weight="bold" className="text-[#C9C4BA] shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <p className="font-display text-[15px] leading-tight text-[#2A0A0A] truncate">{p.title}</p>
                      <p className="text-[11px] text-[#8A8681] flex items-center gap-1 mt-0.5 min-w-0"><MapPin size={11} weight="duotone" className="shrink-0 text-[#B08D57]" /><span className="truncate">{p.address}</span></p>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[#5C5A58] relative z-10">
                    {p.agent && <span className="inline-flex items-center gap-1"><User size={11} weight="duotone" className="text-[#8A8681]" />{p.agent}</span>}
                    {p.photographer && <span className="inline-flex items-center gap-1"><Camera size={11} weight="duotone" className="text-[#8A8681]" />{p.photographer}</span>}
                    {p.pilot && <span className="inline-flex items-center gap-1"><PaperPlaneTilt size={11} weight="duotone" className="text-[#8A8681]" />{p.pilot}</span>}
                    {p.shoot_date && <span className="text-[#9A968E]">{fmtDate(p.shoot_date)}</span>}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5 relative z-10">
                    {p.deliverables_ready && <span className="pill pill--info" data-testid={`kanban-delivered-${p.property_id}`}><CheckCircle size={11} weight="fill" />Delivered</span>}
                    {p.client_paid && <span className="pill pill--paid"><CurrencyDollar size={11} weight="fill" />Paid</span>}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5 relative z-10">
                    <LinkChip href={p.deliverables_url} icon={ArrowSquareOut} label="Agent link" tone="#4A0000" testid={`kanban-agent-link-${p.property_id}`} />
                    <LinkChip href={p.crew_upload_url} icon={UploadSimple} label="Upload link" tone="#8A6D1B" testid={`kanban-upload-link-${p.property_id}`} />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
